import { useRef, useMemo } from "react";
import Modal from "react-modal";
import ModelDashboard from "../../../pages/dashboard/ModelDashboard";
import {
  Sparkles,
  MessageSquare,
  User,
  Bot,
  X,
  Calendar,
  Filter,
  MapPin,
  Tag,
  Settings2,
} from "lucide-react";
import { ExportPDFButton } from "./ExportToPDF";

const customStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    transform: "translate(-50%, -50%)",
    zIndex: 200,
    width: "60vw",
    height: "95vh",
    maxWidth: "1400px",
    maxHeight: "95vh",
    padding: "0",
    border: "none",
    borderRadius: "16px",
    background: "white",
    overflow: "hidden", // ✅ prevent double scroll
  },
  overlay: {
    backgroundColor: "rgba(0,0,0,0.75)",
    zIndex: 199,
  },
};

function RemarksModal({ modalIsOpen, closeModal, modalData }: any) {
  const contentRef = useRef<HTMLDivElement>(null);
  /* ---------------- AI INSIGHTS PARSING ---------------- */
  const aiInsightsArray = useMemo(() => {
    let insights: string[] = [];
    try {
      const content = modalData?.ai_sights_contents;

      if (Array.isArray(content)) {
        insights = content;
      } else if (typeof content === "string") {
        insights = content
          .split(/(?<=\.)\s+|[\n\r]+/)
          .map((i) => i.trim())
          .filter((i) => i.length > 5);
      } else if (modalData?.model_response) {
        const response = modalData.model_response;
        insights = Array.isArray(response)
          ? response
          : JSON.parse(typeof response === "string" ? response : "[]");
      }
    } catch (e) {
      console.error("AI insight parsing error:", e);
    }
    return insights;
  }, [modalData?.ai_sights_contents, modalData?.model_response]);

  return (
    <Modal
      isOpen={modalIsOpen}
      onRequestClose={closeModal}
      style={customStyles}
      contentLabel="AI Insights & Remarks"
    >
      <div className="bg-white rounded-2xl shadow-2xl h-full flex flex-col">
        {/* ================= HEADER ================= */}
        <div className="sticky top-0 z-10 bg-gradient-to-r  px-8 py-6 shadow-lg">
          <div className="flex items-center justify-between">
            {/* Title */}
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/30 backdrop-blur-md rounded-2xl border border-white/20">
                <Sparkles size={28} className="text-blue-600" />
              </div>

              <div>
                <h2 className="text-2xl font-extrabold text-black">
                  {modalData?.chart_title || "Analysis Report"}
                </h2>

                {/* {modalData?.chart_type && (
                  <span className="mt-1 inline-flex items-center gap-2 text-[10px] font-black uppercase px-2 py-0.5 bg-black/10 rounded border border-black/5">
                    <span className="w-1 h-1 bg-blue-600 rounded-full" />
                    {modalData.chart_type}
                  </span>
                )} */}

                {(modalData?.json_data?.chartFilter?.from_date ||
                  modalData?.json_data?.chartFilter?.to_date) && (
                  <div className="mt-1 flex items-center gap-1 text-sm font-bold bg-white/10 py-1.5 rounded-full w-fit">
                    <Calendar size={14} className="text-green-600" />
                    <span>
                      {modalData?.json_data?.chartFilter?.from_date
                        ? new Date(
                            modalData.json_data.chartFilter.from_date
                          ).toLocaleDateString("en-US", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })
                        : "Start"}
                      <span className="mx-2 opacity-60">→</span>
                      {modalData?.json_data?.chartFilter?.to_date
                        ? new Date(
                            modalData.json_data.chartFilter.to_date
                          ).toLocaleDateString("en-US", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })
                        : "End"}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3">
              <ExportPDFButton contentRef={contentRef} modalData={modalData} />
              <button
                onClick={closeModal}
                className="p-2 bg-gray-100 hover:bg-red-50 text-gray-600 hover:text-red-600 rounded-md border transition active:scale-95"
              >
                <X size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* ================= CONTENT ================= */}
        <div
          ref={contentRef}
          className="flex-1 overflow-y-auto p-8 spacfDETAILSe-y-6 bg-gray-50"
        >
          {/* Dashboard */}
          {modalData?.json_data && (
            <div className="bg-white rounded-2xl p-6 shadow border border-blue-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-indigo-600 rounded-xl">
                  <Settings2 size={20} className="text-white" />
                </div>
                <h3 className="text-xl font-bold">Visual Analysis</h3>
              </div>
              <ModelDashboard chartData={modalData.json_data} />
            </div>
          )}

          {/* Filters */}
          {(modalData?.json_data?.chartFilter || modalData?.region) && (
            <div className="bg-white rounded-2xl p-6 shadow border border-blue-100">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-blue-600 rounded-xl">
                  <Filter size={20} className="text-white" />
                </div>
                <h3 className="text-xl font-bold">Applied Filters</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* LOCATION */}
                <FilterBlock
                  title="LOCATION"
                  icon={<MapPin size={16} />}
                  color="blue"
                  filters={[
                    ["Region", "region"],
                    ["District", "district"],
                    ["Taluk", "taluk"],
                    ["Village", "village"],
                  ]}
                  data={modalData}
                />

                {/* CATEGORY */}
                <FilterBlock
                  title="CATEGORIES"
                  icon={<Tag size={16} />}
                  color="purple"
                  filters={[
                    ["Service", "service"],
                    ["Mode", "mode"],
                    ["Land Type", "land_type"],
                    ["Source", "source"],
                  ]}
                  data={modalData}
                />

                {/* DETAILS – show only if drilldown code exists */}
                {/* {modalData?.json_data?.chartFilter
                  ?.drilldownChartRowDistrictCode && (
                  <FilterBlock
                    title="DETAILS"
                    icon={<Settings2 size={16} />}
                    color="green"
                    filters={[
                      ["Citizen Type", "popupChartRtrStrCitizenType"],
                      ["Tab ID", "tabId"],
                      ["Drilldown Code", "drilldownChartRowDistrictCode"],
                      ["District Code", "popupDistrictCode"],
                      ["Taluk Code", "popupTalukCode"],
                    ]}
                    data={modalData.json_data.chartFilter}
                  />
                )} */}
              </div>
            </div>
          )}

          {/* AI INSIGHTS */}
          {aiInsightsArray.length > 0 && (
            <div className="bg-white rounded-2xl p-6 shadow border border-purple-200">
              <div className="flex items-center gap-3 mb-5">
                <div className="p-2.5 bg-purple-600 rounded-xl">
                  <Bot size={22} className="text-white" />
                </div>
                <h3 className="text-xl font-bold">AI-Generated Insights</h3>
              </div>

              <div className="space-y-4">
                {aiInsightsArray.map((insight, idx) => (
                  <div
                    key={idx}
                    className="p-5 bg-purple-50 rounded-xl border border-purple-100"
                  >
                    <div className="flex gap-4">
                      <Sparkles size={18} className="text-purple-600 mt-1" />
                      <p className="text-gray-800 leading-relaxed font-medium">
                        {insight}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* USER REMARKS */}
          {modalData?.user_remarks && (
            <div className="bg-white rounded-2xl p-6 shadow border border-green-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2.5 bg-green-600 rounded-xl">
                  <User size={22} className="text-white" />
                </div>
                <h3 className="text-xl font-bold">User Remarks</h3>
              </div>

              <div className="p-6 bg-green-50 rounded-xl border">
                <div className="flex gap-4">
                  <MessageSquare size={22} className="text-green-600" />
                  <p className="whitespace-pre-wrap text-gray-800">
                    {modalData.user_remarks}
                  </p>
                </div>
              </div>

              {modalData?.created_at && (
                <p className="mt-3 text-sm text-gray-600">
                  Submitted on{" "}
                  {new Date(modalData.created_at).toLocaleString("en-US")}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

/* ---------------- FILTER BLOCK ---------------- */
function FilterBlock({ title, icon, color, filters, data }: any) {
  return (
    <div>
      <div
        className={`flex items-center gap-2 text-sm font-bold text-${color}-600 mb-2`}
      >
        {icon}
        {title}
      </div>

      <div className="space-y-2">
        {filters.map(([label, key]: any) => {
          const rawValue = data?.[key];
          const isEmpty =
            rawValue === null ||
            rawValue === undefined ||
            rawValue === "" ||
            (Array.isArray(rawValue) && rawValue.length === 0);

          return (
            <div key={key} className={`p-2.5 bg-${color}-50 rounded-xl border`}>
              <span className="text-xs italic text-gray-500">{label}</span>
              <div className="font-bold text-sm">
                {isEmpty
                  ? "All"
                  : Array.isArray(rawValue)
                  ? rawValue.join(", ")
                  : rawValue}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default RemarksModal;
